import { gsap } from "gsap";

/**
 * Accordion — GSAP height animation on each panel.
 *
 * Replaces the block's original Alpine.js directives (x-show / x-collapse),
 * which never ran because Alpine was not installed on this site.
 *
 * Panels animate between 0 and their natural height. Height is measured live on
 * each open rather than cached, so panels that change size (responsive reflow,
 * a font swapping in, images loading) still animate to the right value. On
 * completion the height is cleared to `auto` so the panel can grow afterwards.
 *
 * Rows open independently — opening one does not close the others, matching the
 * reference where several sit open at once.
 *
 * Accessibility: `hidden` is removed before measuring and reapplied after the
 * close finishes, so a closed panel stays out of both the a11y tree and tab
 * order. aria-expanded tracks state on the trigger.
 *
 * No-ops when the markup isn't present.
 */
function initAccordion() {
  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  document.querySelectorAll("[data-accordion]").forEach((root) => {
    // Guard against double-init if called twice on the same DOM.
    if (root.hasAttribute("data-accordion-initialized")) return;
    root.setAttribute("data-accordion-initialized", "");

    root.querySelectorAll("[data-accordion-item]").forEach((item) => {
      const trigger = item.querySelector("[data-accordion-trigger]");
      const panel = item.querySelector("[data-accordion-panel]");
      if (!trigger || !panel) return;

      // The answer indents by the same 20px as its heading when open. Driven
      // here rather than in CSS: on open the panel goes from `hidden` to
      // visible in a single frame, so a CSS transition has no start value to
      // animate from and the text snapped into place.
      const INDENT = 20;
      const content = panel.querySelector(".accordion-content");

      const setOpen = (open) => {
        trigger.setAttribute("aria-expanded", String(open));

        // Kill any in-flight tween so rapid clicks can't leave a panel stuck
        // mid-height or race the onComplete cleanup below.
        gsap.killTweensOf(panel);
        if (content) gsap.killTweensOf(content);

        if (open) {
          // Unhide before measuring — a hidden element reports zero height.
          panel.hidden = false;

          if (reduceMotion) {
            gsap.set(panel, { height: "auto" });
            if (content) gsap.set(content, { x: INDENT });
            return;
          }

          if (content) {
            gsap.fromTo(
              content,
              { x: 0 },
              { x: INDENT, duration: 0.45, ease: "power2.out" },
            );
          }

          gsap.fromTo(
            panel,
            { height: 0 },
            {
              height: panel.scrollHeight,
              duration: 0.45,
              ease: "power2.out",
              // Back to auto so later content/reflow isn't clipped to the
              // pixel height we just animated to.
              onComplete: () => gsap.set(panel, { height: "auto" }),
            },
          );
          return;
        }

        if (reduceMotion) {
          panel.hidden = true;
          if (content) gsap.set(content, { x: 0 });
          return;
        }

        if (content) {
          gsap.to(content, { x: 0, duration: 0.35, ease: "power2.inOut" });
        }

        gsap.fromTo(
          panel,
          { height: panel.scrollHeight },
          {
            height: 0,
            duration: 0.35,
            ease: "power2.inOut",
            onComplete: () => {
              panel.hidden = true;
              // Clear the inline 0 so the next open measures correctly.
              gsap.set(panel, { clearProps: "height" });
            },
          },
        );
      };

      trigger.addEventListener("click", () => {
        setOpen(trigger.getAttribute("aria-expanded") !== "true");
      });
    });
  });
}

/**
 * Quick-nav for grouped accordions.
 *
 * The nav itself is rendered server-side (see blocks/accordion.antlers.html), so
 * its links work as plain #anchors with JS off. This adds two things on top:
 *
 *   1. Scroll-spy — highlights whichever group is currently in view.
 *   2. Collapse — the chevron beside each nav entry hides that group's rows.
 *
 * Scrolling is NOT handled here: the links are real anchors and initLenis() in
 * site.js already smooth-scrolls every `a[href^="#"]`.
 *
 * No-ops when no grouped accordion is on the page.
 */
function initAccordionNav() {
  document.querySelectorAll("[data-accordion-nav]").forEach((nav) => {
    if (nav.hasAttribute("data-accordion-nav-initialized")) return;
    nav.setAttribute("data-accordion-nav-initialized", "");

    const block = nav.closest(".block__accordion");
    if (!block) return;

    // --- Collapse toggles ---------------------------------------------
    nav.querySelectorAll("[data-accordion-group-toggle]").forEach((toggle) => {
      const groupId = toggle.getAttribute("data-accordion-group-toggle");
      const group = block.querySelector(`[data-accordion-group="${groupId}"]`);
      if (!group) return;

      toggle.addEventListener("click", () => {
        const collapsed = group.hasAttribute("data-accordion-collapsed");

        if (collapsed) {
          group.removeAttribute("data-accordion-collapsed");
        } else {
          group.setAttribute("data-accordion-collapsed", "");
        }

        // aria-expanded describes the CONTROLLED region, so it is the inverse
        // of the collapsed flag we just set.
        toggle.setAttribute("aria-expanded", String(collapsed));
      });
    });

    // NOTE: no scroll handler here. The nav links are real `a[href="#id"]`
    // anchors, and initLenis() in site.js already binds smooth scrolling to
    // every `a[href^="#"]` on the page with the correct header offset. Adding a
    // second handler would double-bind and fight it for control of the scroll.

    // --- Scroll-spy -----------------------------------------------------
    const groups = Array.from(block.querySelectorAll("[data-accordion-group]"));
    if (!groups.length) return;

    const setActive = (groupId) => {
      nav.querySelectorAll("[data-accordion-nav-link]").forEach((link) => {
        const item = link.closest(".block__accordion__nav-item");
        if (!item) return;
        const isActive = link.getAttribute("data-accordion-nav-link") === groupId;
        item.classList.toggle("block__accordion__nav-item--active", isActive);
      });
    };

    // rootMargin pulls the detection line down to just under the sticky header
    // and up from the bottom, so the "current" group is the one occupying the
    // upper part of the viewport rather than whatever merely touches its edge.
    const observer = new IntersectionObserver(
      (entries) => {
        // Several groups can be on screen at once; the topmost visible one wins,
        // which is what a reader perceives as the section they are in.
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length) {
          setActive(visible[0].target.getAttribute("data-accordion-group"));
        }
      },
      { rootMargin: "-20% 0px -60% 0px", threshold: 0 },
    );

    groups.forEach((group) => observer.observe(group));
  });
}

export { initAccordion, initAccordionNav };
